/**
 * client/audio/audio.ts — Web Audio engine for BO2 sound aliases
 * (game/public/assets/audio/manifest.json, research/audio.md).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * Signal flow: voice source -> gain (alias volume x distance) -> [PannerNode (HRTF) for 3D] ->
 *   bus gain (sfx / music / voice, from Settings.volume) -> master gain -> limiter -> output,
 *   plus an optional reverb send (one ConvolverNode with a synthetic tail; the per-room T6 reverb
 *   presets are not converted - ESTIMATE).
 * Positions are CoD space (Z up); the listener uses the refdef eye and view axes. Web Audio is
 * right-handed like CoD space, so the vectors are passed through unchanged.
 * Distance attenuation is ours (alias curves, alias.ts); the PannerNode only pans
 * (distanceModel 'linear', rolloffFactor 0).
 *
 * Unlock: init() is idempotent, creates the context and calls resume() WITHOUT awaiting it (a
 * resume outside a user gesture never resolves); the app calls it during load and on early
 * pointerdown/keydown.
 */
import { BOLT_BEEP_DIV, BOLT_BEEP_MIN_MS, BOLT_BEEP_START_MS } from '@shared/constants';
import { type Vec3, angleVectors } from '@shared/math';
import {
  GameEventType, MatchPhase, MissileKind, MissileState, PmType, WeaponId, type GameEvent,
} from '@shared/types';
import type { AudioEngine, ClientWorld, PlayOptions, RefDef } from '../types';
import type { Settings } from '../settings/settings';
import { type AliasDef, type AudioManifest, aliasGain, centsToRate, distanceGains, limitDecision, pickVariant, randRange } from './alias';
import { aliasesForPEv, announcerCandidates, bounceAliases, impactAliases, voicePrefix, weaponSound } from './events';
import { MAP_EMITTERS } from './emitters.generated';

/** Closest point of segment a-b to p (CoD space), written to out. */
export function closestOnSegment(out: Vec3, a: readonly number[], b: readonly number[], p: readonly number[]): Vec3 {
  const dx = b[0]! - a[0]!, dy = b[1]! - a[1]!, dz = b[2]! - a[2]!;
  const len2 = dx * dx + dy * dy + dz * dz;
  let t = len2 > 0 ? ((p[0]! - a[0]!) * dx + (p[1]! - a[1]!) * dy + (p[2]! - a[2]!) * dz) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  out[0] = a[0]! + dx * t; out[1] = a[1]! + dy * t; out[2] = a[2]! + dz * t;
  return out;
}

const BASE = '/assets/audio/';
/** Global voice cap (the engine mixes far more; this keeps the browser happy). */
const MAX_VOICES = 64;
/** A sound whose buffer arrives later than this is dropped (one-shots only). */
const LATE_DROP_S = 0.25;
/** Overall makeup gain into the limiter: aliases are mixed at dBspl with 100 = full scale, so the
 *  in-game mix sits around -10..-40 dBFS. ESTIMATE, chosen by ear against a BO2 capture. */
const MAKEUP_GAIN = 2.0;
/** Categories decoded right after the manifest loads (everything gameplay needs). */
const PRELOAD_PREFIXES = [
  'amb_',
  'wpn_crossbow', 'wpn_bal_knife', 'wpn_hatchet', 'wpn_melee', 'wpn_com_knife', 'wpn_knife', 'wpn_grenade_explode', 'wpn_semtex',
  'prj_bolt_impact_', 'fly_step_run_', 'fly_step_sprint_', 'fly_step_walk_', 'fly_land_', 'fly_gear_', 'fly_generic_', 'fly_equipment',
  'fly_crossbow', 'fly_bal_knife', 'mpl_', 'mus_lau_medal', 'uin_', 'vox_st6_sns', 'vox_pmc_sns', 'amb_wind_extreior_2d',
];

/** sas.gsc game["dialog"]["wm_humiliation"]. */
const BANKRUPT_STING = 'mpl_wager_bankrupt';
/** Two bankrupt events closer than this are one kill (ESTIMATE; the sting is 2.41 s long). */
const STING_DEDUPE_S = 0.5;
/** Voice entity keys for missiles (players use their clientNum, 0..63). */
const MISSILE_ENT_BASE = 1 << 16;
export function missileEntKey(entId: number): number { return MISSILE_ENT_BASE + entId; }

/** Does this BANKRUPT event give the local player the mpl_wager_bankrupt sting? (axe kill: attacker + victim) */
export function bankruptStingFor(ge: { attacker: number; victim: number }, me: number): boolean {
  return ge.attacker >= 0 && ge.attacker !== ge.victim && (ge.victim === me || ge.attacker === me);
}

interface Voice {
  id: number;
  name: string;
  def: AliasDef;
  src: AudioBufferSourceNode | null;
  gain: GainNode;
  wet: GainNode | null;
  panner: PannerNode | null;
  pos: Vec3 | null;
  ent: number;
  follow: (() => Vec3 | null) | null;
  baseGain: number;
  priority: number;
  start: number;
  ended: boolean;
  pendingStop: boolean;
  /** Secondary-alias voices started with this one (stopped with it). */
  children: number[];
}

export class Bo2Audio implements AudioEngine {
  private ctx: AudioContext | null = null;
  private manifest: AudioManifest | null = null;
  private manifestLoad: Promise<void> | null = null;
  private master: GainNode | null = null;
  private buses: Record<'sfx' | 'music' | 'voice', GainNode> | null = null;
  private reverb: ConvolverNode | null = null;
  private readonly buffers = new Map<string, AudioBuffer | null>();
  private readonly loading = new Map<string, Promise<AudioBuffer | null>>();
  private ext: 'ogg' | 'mp3' = 'ogg';
  private readonly voices = new Map<number, Voice>();
  private nextId = 1;
  private vol: Settings['volume'] = { master: 0.8, sfx: 1, music: 0.6, voice: 1 };
  // per-frame state
  private world: ClientWorld | null = null;
  private readonly listener: Vec3 = [0, 0, 0];
  private readonly fwd: Vec3 = [1, 0, 0];
  private readonly up: Vec3 = [0, 0, 1];
  private ambienceStarted = false;
  private readonly missileLoops = new Map<number, number>();
  private readonly boltBeeps = new Map<number, { next: number; interval: number }>();
  private lastPhase = -1;
  private lastTimerSec = -1;
  private timeOutMusic = false;
  private endMusic = false;
  private healthClock = { breathAt: 0, heartAt: 0, heartWait: 0.2, hurtSince: -1 };
  private readonly recentImpacts: { key: string; t: number }[] = [];
  private rnd: () => number = Math.random;

  async init(): Promise<void> {
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor({ latencyHint: 'interactive' });
        this.buildGraph();
        // debug/test handle (window.__bo2audio): voice count, context state, manifest status
        (window as unknown as { __bo2audio?: Bo2Audio }).__bo2audio = this;
        const probe = document.createElement('audio');
        this.ext = probe.canPlayType('audio/ogg; codecs="opus"') ? 'ogg' : 'mp3';
      }
      // never await: without a user gesture this promise does not resolve
      if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
      this.manifestLoad ??= this.loadManifest();
    } catch {
      this.ctx = null;
    }
  }

  private buildGraph(): void {
    const ctx = this.ctx!;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3; limiter.knee.value = 3; limiter.ratio.value = 12;
    limiter.attack.value = 0.002; limiter.release.value = 0.15;
    limiter.connect(ctx.destination);
    this.master = ctx.createGain();
    this.master.connect(limiter);
    const mk = () => { const g = ctx.createGain(); g.connect(this.master!); return g; };
    this.buses = { sfx: mk(), music: mk(), voice: mk() };
    // synthetic reverb tail (ESTIMATE; T6 reverb presets not converted)
    try {
      const len = Math.floor(ctx.sampleRate * 1.1);
      const ir = ctx.createBuffer(2, len, ctx.sampleRate);
      for (let c = 0; c < 2; c++) {
        const d = ir.getChannelData(c);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2) * 0.35;
      }
      this.reverb = ctx.createConvolver();
      this.reverb.buffer = ir;
      this.reverb.connect(this.buses.sfx);
    } catch { this.reverb = null; }
    this.applyVolumes();
  }

  private async loadManifest(): Promise<void> {
    try {
      const r = await fetch(`${BASE}manifest.json`);
      if (!r.ok) return;
      this.manifest = (await r.json()) as AudioManifest;
      // preload gameplay aliases in the background
      const files = new Set<string>();
      for (const [name, a] of Object.entries(this.manifest.aliases)) {
        if (a.storage === 'streamed' && a.isMusic) continue;
        if (PRELOAD_PREFIXES.some((p) => name.startsWith(p))) for (const v of a.variants) files.add(v.file);
      }
      let i = 0;
      const list = [...files];
      const pump = () => {
        // a few at a time so gameplay fetches are not starved
        for (let n = 0; n < 6 && i < list.length; n++, i++) void this.buffer(list[i]!);
        if (i < list.length) setTimeout(pump, 30);
      };
      pump();
    } catch {
      this.manifest = null;
    }
  }

  private buffer(file: string): Promise<AudioBuffer | null> {
    const have = this.buffers.get(file);
    if (have !== undefined) return Promise.resolve(have);
    let p = this.loading.get(file);
    if (p) return p;
    p = this.fetchDecode(file, this.ext).then((b) => b ?? (this.ext === 'ogg' ? this.fetchDecode(file, 'mp3') : null))
      .then((b) => { this.buffers.set(file, b); this.loading.delete(file); return b; });
    this.loading.set(file, p);
    return p;
  }

  private async fetchDecode(file: string, ext: 'ogg' | 'mp3'): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    try {
      const r = await fetch(`${BASE}${file}.${ext}`);
      if (!r.ok) return null;
      const data = await r.arrayBuffer();
      return await this.ctx.decodeAudioData(data);
    } catch {
      if (ext === 'ogg') this.ext = 'mp3';
      return null;
    }
  }

  alias(name: string): AliasDef | null {
    return this.manifest?.aliases[name] ?? null;
  }

  /** First alias of the list that exists in the manifest (with audio). */
  private firstAlias(names: readonly string[]): string | null {
    for (const n of names) {
      const a = this.alias(n);
      if (a && a.variants.length) return n;
    }
    return null;
  }

  // =========================================================================================
  play(alias: string, opts: PlayOptions = {}): number | null {
    return this.playInternal(alias, opts.pos ?? null, opts.ent ?? -1, null, opts.volume ?? 1, 0);
  }

  private playInternal(name: string, pos: Vec3 | null, ent: number, follow: (() => Vec3 | null) | null, volume: number, depth: number): number | null {
    const ctx = this.ctx, m = this.manifest;
    if (!ctx || !m || !this.buses) return null;
    const a = m.aliases[name];
    if (!a) return null;
    // secondary alias starts together with this one (e.g. *_fire_npc -> *_fire_dist)
    const child = a.secondary && depth < 3 ? this.playInternal(a.secondary, pos, ent, follow, volume, depth + 1) : null;
    if (!a.variants.length) return child; // deliberately silent alias (its secondary may still play)
    // while the context is still locked (no user gesture yet) one-shots would all fire at once on
    // unlock: drop them; loops (ambience, music) are kept and start when it resumes
    if (ctx.state !== 'running' && !a.looping) return child;
    if (a.probability < 1 && this.rnd() > a.probability) return null;
    const is3d = a.pan === '3d' && (pos !== null || follow !== null || (ent >= 0 && ent !== this.world?.localClientNum));
    const priority = randRange(a.priority, this.rnd());
    // limits: global per alias, then per entity
    const active: Voice[] = [];
    const activeEnt: Voice[] = [];
    for (const v of this.voices.values()) {
      if (v.name !== name || v.ended) continue;
      active.push(v);
      if (ent >= 0 && v.ent === ent) activeEnt.push(v);
    }
    const dec = limitDecision(active.map((v) => ({ id: v.id, priority: v.priority, start: v.start })), a.limitCount, a.limitType, priority);
    if (!dec.play) return null;
    if (ent >= 0) {
      const de = limitDecision(activeEnt.map((v) => ({ id: v.id, priority: v.priority, start: v.start })), a.entityLimitCount, a.entityLimitType, priority);
      if (!de.play) return null;
      if (de.steal !== null) this.stop(de.steal);
    }
    if (dec.steal !== null) this.stop(dec.steal);
    if (this.voices.size >= MAX_VOICES) {
      let lo: Voice | null = null;
      for (const v of this.voices.values()) if (!v.def.looping && (!lo || v.priority < lo.priority)) lo = v;
      if (!lo || lo.priority > priority) return null;
      this.stop(lo.id);
    }

    const vi = pickVariant(a.variants.length, this.rnd());
    const variant = a.variants[vi]!;
    const gain = ctx.createGain();
    const baseGain = aliasGain(a, m.globals.volumeGroups, this.rnd()) * volume;
    let panner: PannerNode | null = null;
    if (is3d) {
      panner = ctx.createPanner();
      panner.panningModel = 'HRTF';
      panner.distanceModel = 'linear';
      panner.rolloffFactor = 0;
      panner.refDistance = 1; panner.maxDistance = 1e7;
      gain.connect(panner);
      panner.connect(this.busFor(a));
    } else {
      gain.connect(this.busFor(a));
    }
    let wet: GainNode | null = null;
    if (this.reverb && a.reverbSendDb > 0) {
      wet = ctx.createGain();
      gain.connect(wet);
      wet.connect(this.reverb);
    }
    const id = this.nextId++;
    if (this.played.length < 2000) this.played.push(name);
    const v: Voice = {
      id, name, def: a, src: null, gain, wet, panner, pos: pos ? [pos[0], pos[1], pos[2]] : null, ent, follow,
      baseGain, priority, start: ctx.currentTime, ended: false, pendingStop: false, children: child !== null ? [child] : [],
    };
    this.voices.set(id, v);
    this.updateVoice(v);
    const startAt = ctx.currentTime + (a.startDelayMs > 0 ? a.startDelayMs / 1000 : 0);
    const requested = ctx.currentTime;
    const rate = a.rate ? randRange(a.rate, this.rnd()) : centsToRate(randRange(a.pitchCents, this.rnd()));
    const go = (buf: AudioBuffer | null) => {
      if (!buf || v.ended || v.pendingStop || !this.ctx) { this.finish(v); return; }
      const late = this.ctx.currentTime - requested;
      if (!a.looping && late > LATE_DROP_S) { this.finish(v); return; }
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = a.looping;
      src.playbackRate.value = rate;
      src.connect(gain);
      src.onended = () => this.finish(v);
      v.src = src;
      const t = Math.max(this.ctx.currentTime, startAt);
      if (a.fadeInMs > 0) {
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(gain.gain.value || baseGain, t + a.fadeInMs / 1000);
      }
      src.start(t);
    };
    const have = this.buffers.get(variant.file);
    if (have !== undefined) go(have);
    else void this.buffer(variant.file).then(go);
    return id;
  }

  private busFor(a: AliasDef): GainNode {
    const b = this.buses!;
    if (a.bus === 'bus_music' || a.isMusic) return b.music;
    if (a.bus === 'bus_voice') return b.voice;
    return b.sfx;
  }

  stop(handle: number): void {
    const v = this.voices.get(handle);
    if (!v || v.ended) return;
    for (const c of v.children) this.stop(c);
    const ctx = this.ctx;
    if (v.src && ctx && v.def.fadeOutMs > 0) {
      v.pendingStop = true;
      const t = ctx.currentTime;
      v.gain.gain.cancelScheduledValues(t);
      v.gain.gain.setValueAtTime(v.gain.gain.value, t);
      v.gain.gain.linearRampToValueAtTime(0, t + v.def.fadeOutMs / 1000);
      try { v.src.stop(t + v.def.fadeOutMs / 1000); } catch { /* already stopped */ }
      return;
    }
    v.pendingStop = true;
    try { v.src?.stop(); } catch { /* not started */ }
    this.finish(v);
  }

  private finish(v: Voice): void {
    if (v.ended) return;
    v.ended = true;
    this.voices.delete(v.id);
    try { v.src?.disconnect(); } catch { /* ignore */ }
    v.gain.disconnect();
    v.panner?.disconnect();
    v.wet?.disconnect();
  }

  /** Recompute distance gain + panner position of one voice. */
  private updateVoice(v: Voice): void {
    const m = this.manifest;
    if (!m || v.pendingStop) return;
    let g = v.baseGain;
    let wetG = 0;
    if (v.panner) {
      const p = v.follow?.() ?? (v.ent >= 0 ? this.entPos(v.ent) : null) ?? v.pos;
      if (p) {
        v.pos = v.pos ?? [0, 0, 0];
        v.pos[0] = p[0]; v.pos[1] = p[1]; v.pos[2] = p[2];
        const dx = p[0] - this.listener[0], dy = p[1] - this.listener[1], dz = p[2] - this.listener[2];
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const dg = distanceGains(v.def, m.globals.curves, d);
        g *= dg.dry;
        wetG = dg.wet;
        const t = this.ctx!.currentTime;
        v.panner.positionX.setValueAtTime(p[0], t);
        v.panner.positionY.setValueAtTime(p[1], t);
        v.panner.positionZ.setValueAtTime(p[2], t);
      }
    } else wetG = 1;
    if (!(v.def.fadeInMs > 0 && v.src)) v.gain.gain.value = g;
    if (v.wet) v.wet.gain.value = v.baseGain * wetG * Math.pow(10, (v.def.reverbSendDb - 100) / 20) * 0.5;
  }

  private entPos(ent: number): Vec3 | null {
    const w = this.world;
    if (!w) return null;
    if (ent >= MISSILE_ENT_BASE) return null; // missile voices follow the missile or keep their start pos
    if (ent === w.localClientNum) return this.listener;
    const p = w.getPlayer(ent);
    if (!p) return null;
    return [p.origin[0], p.origin[1], p.origin[2] + Math.min(48, p.viewHeight || 48)];
  }

  setVolumes(v: Settings['volume']): void {
    this.vol = { ...v };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (!this.master || !this.buses) return;
    this.master.gain.value = Math.max(0, this.vol.master) * MAKEUP_GAIN;
    this.buses.sfx.gain.value = Math.max(0, this.vol.sfx);
    this.buses.music.gain.value = Math.max(0, this.vol.music);
    this.buses.voice.gain.value = Math.max(0, this.vol.voice);
  }

  dispose(): void {
    for (const v of [...this.voices.values()]) this.stop(v.id);
    void this.ctx?.close();
    this.ctx = null;
  }

  // =========================================================================================
  update(world: ClientWorld, refdef: RefDef, settings: Settings, _dtMs: number): void {
    this.world = world;
    if (settings.volume.master !== this.vol.master || settings.volume.sfx !== this.vol.sfx
      || settings.volume.music !== this.vol.music || settings.volume.voice !== this.vol.voice) this.setVolumes(settings.volume);
    const ctx = this.ctx;
    if (!ctx || !this.manifest) return;
    // listener = eye, view axes (CoD space)
    this.listener[0] = refdef.origin[0]; this.listener[1] = refdef.origin[1]; this.listener[2] = refdef.origin[2];
    angleVectors(refdef.angles, this.fwd, null, this.up);
    const L = ctx.listener;
    const t = ctx.currentTime;
    if (L.positionX) {
      L.positionX.setValueAtTime(this.listener[0], t); L.positionY.setValueAtTime(this.listener[1], t); L.positionZ.setValueAtTime(this.listener[2], t);
      L.forwardX.setValueAtTime(this.fwd[0], t); L.forwardY.setValueAtTime(this.fwd[1], t); L.forwardZ.setValueAtTime(this.fwd[2], t);
      L.upX.setValueAtTime(this.up[0], t); L.upY.setValueAtTime(this.up[1], t); L.upZ.setValueAtTime(this.up[2], t);
    } else {
      L.setPosition(this.listener[0], this.listener[1], this.listener[2]);
      L.setOrientation(this.fwd[0], this.fwd[1], this.fwd[2], this.up[0], this.up[1], this.up[2]);
    }

    for (const fe of world.frameEvents) {
      if (fe.src === 'pred') this.playPEv(fe.ev, fe.parm, true, world.localClientNum);
      else if (fe.src === 'ent') this.playPEv(fe.ev, fe.parm, fe.ent === world.localClientNum, fe.ent);
      else this.onGameEvent(world, fe.ge);
    }
    this.ambience(world);
    this.randomEmitters();
    this.missiles(world);
    this.matchSounds(world);
    this.healthSounds(world);
    for (const v of this.voices.values()) if (v.panner) this.updateVoice(v);
  }

  private playPEv(ev: number, parm: number, firstPerson: boolean, ent: number): void {
    const name = this.firstAlias(aliasesForPEv(ev, parm, firstPerson));
    if (!name) return;
    if (firstPerson) this.playInternal(name, null, -1, null, 1, 0);
    else this.playInternal(name, null, ent, null, 1, 0);
  }

  private bodyOf(world: ClientWorld, cn: number): number {
    return world.playerInfo.get(cn)?.body ?? 0;
  }

  private onGameEvent(world: ClientWorld, ge: GameEvent): void {
    const me = world.localClientNum;
    switch (ge.type) {
      case GameEventType.HITMARKER:
        this.playInternal('mpl_hit_alert', null, -1, null, 1, 0); // _damagefeedback.gsc
        break;
      case GameEventType.MEDAL:
        this.playInternal('mus_lau_medal', null, -1, null, 1, 0); // notificationpopups.lua MedalNotifyStart
        break;
      case GameEventType.EXPLOSION: {
        const s = weaponSound(WeaponId.EXPLOSIVE_BOLT, 'projExplosionSound', false) || 'wpn_grenade_explode';
        this.playInternal(s, ge.pos, -1, null, 1, 0);
        break;
      }
      case GameEventType.IMPACT: {
        if (this.dedupeImpact(ge.pos)) break;
        const n = this.firstAlias(impactAliases(ge.weapon, ge.surface));
        if (n) this.playInternal(n, ge.pos, -1, null, 1, 0);
        break;
      }
      case GameEventType.MISSILE_STICK: {
        if (this.dedupeImpact(ge.pos)) break;
        const n = this.firstAlias(impactAliases(ge.weapon, ge.stuckTo >= 0 && ge.stuckTo < 255 ? 29 /* player */ : ge.surface));
        if (n) this.playInternal(n, ge.pos, -1, null, 1, 0);
        break;
      }
      case GameEventType.MISSILE_BOUNCE: {
        // EV_GRENADE_BOUNCE plays on the missile entity, so the alias entity limit applies per hatchet:
        // wpn_hatchet_bounce_* have entityLimitCount 1 / entityLimitType 'reject' (audio manifest), i.e. a
        // bounce while the previous one is still sounding is dropped (no rattle on ground skitters).
        const n = this.firstAlias(bounceAliases(ge.weapon, ge.surface));
        const id = ge.entId;
        if (n) this.playInternal(n, ge.pos, missileEntKey(id), () => this.missilePos(id), 1, 0);
        break;
      }
      case GameEventType.MELEE_HIT: {
        const w = ge.weapon || WeaponId.KNIFE;
        const n = weaponSound(w, 'meleeHitSound', false) || 'wpn_melee_knife_hit_body';
        this.playInternal(n, null, ge.victim === me ? -1 : ge.victim, null, 1, 0);
        break;
      }
      case GameEventType.PICKUP: {
        const local = ge.ent === me;
        const n = weaponSound(ge.weapon, 'pickupSound', local);
        if (n) this.playInternal(n, null, local ? -1 : ge.ent, null, 1, 0);
        break;
      }
      case GameEventType.PAIN: {
        if (ge.ent === me) break; // the victim does not hear its own 3D pain vox (painvox is playsound on the entity)
        this.playInternal(`${voicePrefix(this.bodyOf(world, ge.ent), ge.ent)}_pain_small`, null, ge.ent, null, 1, 0);
        break;
      }
      case GameEventType.DEATH: {
        this.playInternal(`${voicePrefix(this.bodyOf(world, ge.ent), ge.ent)}_pain_death`, null, ge.ent === me ? -1 : ge.ent, null, 1, 0);
        break;
      }
      case GameEventType.BANKRUPT: {
        // sas.gsc onplayerkilled, setback weapon (hatchet) kill: attacker playlocalsound + self playlocalsound
        // ("mpl_wager_bankrupt") [SNS §4-5, research/audio.md Announcer]. The gametype only sends BANKRUPT, so
        // the sting is driven from it here; a SOUND mpl_wager_bankrupt for the same kill is de-duplicated.
        // Self-bankrupt (attacker -1) has no sting in sas.gsc (only sns_hum, handled as SOUND).
        if (bankruptStingFor(ge, me)) this.playBankruptSting();
        break;
      }
      case GameEventType.ANNOUNCER: {
        const n = this.firstAlias(announcerCandidates(ge.alias, this.bodyOf(world, me)));
        if (n) this.playInternal(n, null, -1, null, 1, 0);
        break;
      }
      case GameEventType.SOUND: {
        if (ge.alias === BANKRUPT_STING) { this.playBankruptSting(); break; }
        // a bare dialog key that is not an alias (sas.gsc self-bankrupt playlocalsound("sns_hum")) is voiced
        // with the faction announcer like the vox_wm_ lines (announcerCandidates; remake substitution, ESTIMATE)
        const name = this.alias(ge.alias) ? ge.alias : this.firstAlias(announcerCandidates(ge.alias, this.bodyOf(world, me)));
        if (!name) break;
        if (ge.pos) this.playInternal(name, ge.pos, -1, null, 1, 0);
        else this.playInternal(name, null, ge.ent >= 0 && ge.ent !== me ? ge.ent : -1, null, 1, 0);
        break;
      }
      case GameEventType.SPAWN:
        // _globallogic_audio.gsc: wager spawns set the SPAWN_WAGER music state, whose alias
        // (mus_spawn_wager) does not exist -> silent, exactly like the shipped game
        if (ge.ent === me) this.musicState('SPAWN_WAGER');
        break;
      case GameEventType.SPLASH:
        this.playInternal('mpl_splash_death', ge.pos, -1, null, 1, 0); // mp_hijacked.gsc water kill trigger
        break;
      case GameEventType.TOP3:
        // _wager.gsc plays vox_wm_boost_gen_06/07, which do not exist (silent); the sting that
        // _globallogic_audio.gsc registers for the same dialog keys is used instead (ESTIMATE)
        this.playInternal(ge.entered ? 'mpl_wager_top3_gain' : 'mpl_wager_top3_loss', null, -1, null, 1, 0);
        break;
      default:
        break;
    }
  }

  private lastStingAt = -1;
  /** mpl_wager_bankrupt once per kill (BANKRUPT and a possible gametype SOUND for the same kill). */
  private playBankruptSting(): void {
    const now = this.ctx?.currentTime ?? 0;
    if (this.lastStingAt >= 0 && now - this.lastStingAt < STING_DEDUPE_S) return;
    this.lastStingAt = now;
    this.playInternal(BANKRUPT_STING, null, -1, null, 1, 0);
  }

  /** IMPACT and MISSILE_STICK can describe the same hit: play one sound per spot/instant. */
  private dedupeImpact(pos: Vec3): boolean {
    const now = this.ctx?.currentTime ?? 0;
    const key = `${Math.round(pos[0] / 8)},${Math.round(pos[1] / 8)},${Math.round(pos[2] / 8)}`;
    for (let i = this.recentImpacts.length - 1; i >= 0; i--) if (now - this.recentImpacts[i]!.t > 0.15) this.recentImpacts.splice(i, 1);
    if (this.recentImpacts.some((r) => r.key === key)) return true;
    this.recentImpacts.push({ key, t: now });
    return false;
  }

  // ---- missiles: bolt flight loop + explosive-bolt beeps ---------------------------------------
  private missiles(world: ClientWorld): void {
    const seen = new Set<number>();
    const now = world.renderTime;
    for (const m of world.missiles) {
      seen.add(m.entId);
      // crossbow_mp projectileSound = wpn_crossbow_whoosh_dist (looping) while flying
      if (m.kind === MissileKind.BOLT && m.state === MissileState.FLYING) {
        if (!this.missileLoops.has(m.entId)) {
          const id = m.entId;
          const s = weaponSound(WeaponId.CROSSBOW, 'projectileSound', false);
          const h = s ? this.playInternal(s, null, -1, () => this.missilePos(id), 1, 0) : null;
          this.missileLoops.set(m.entId, h ?? -1);
        }
      } else if (this.missileLoops.has(m.entId)) {
        this.stopLoop(m.entId);
      }
      // _explosive_bolt.csc fx_think: wpn_semtex_alert every `interval`, 0.3 s then /1.2, clamped [0.08, 0.3]
      if (m.kind === MissileKind.BOLT && m.state !== MissileState.FLYING && m.explodeAt > 0) {
        let b = this.boltBeeps.get(m.entId);
        if (!b) { b = { next: now, interval: BOLT_BEEP_START_MS }; this.boltBeeps.set(m.entId, b); }
        while (b.next <= now && b.next < m.explodeAt) {
          if (now - b.next < 100) this.playInternal('wpn_semtex_alert', [m.origin[0], m.origin[1], m.origin[2]], -1, null, 1, 0);
          b.next += b.interval;
          b.interval = Math.min(BOLT_BEEP_START_MS, Math.max(BOLT_BEEP_MIN_MS, b.interval / BOLT_BEEP_DIV));
        }
      }
    }
    for (const id of [...this.missileLoops.keys()]) if (!seen.has(id)) this.stopLoop(id);
    for (const id of [...this.boltBeeps.keys()]) if (!seen.has(id)) this.boltBeeps.delete(id);
  }

  private missilePos(entId: number): Vec3 | null {
    const m = this.world?.missiles.find((x) => x.entId === entId);
    return m ? m.origin : null;
  }

  private stopLoop(entId: number): void {
    const h = this.missileLoops.get(entId);
    this.missileLoops.delete(entId);
    if (h !== undefined && h >= 0) this.stop(h); // also stops the whoosh_close secondary
  }

  // ---- ambience (mp_hijacked_amb.csc via manifest.map.mp_hijacked) -----------------------------
  private ambience(world: ClientWorld): void {
    if (this.ambienceStarted || !this.manifest || !world.map) return;
    const map = this.manifest.map[world.map];
    if (!map) return;
    this.ambienceStarted = true;
    for (const room of Object.values(map.rooms)) {
      if (room.default && room.tone) this.playInternal(room.tone.alias, null, -1, null, 1, 0);
    }
    for (const e of map.loopEmitters) this.playInternal(e.alias, [e.origin[0], e.origin[1], e.origin[2]], -1, null, 1, 0);
    // map-ent emitters (mp_hijacked.d3dbsp script_structs, started by clientscripts/mp/_audio.csc):
    // ocean + wake line emitters, engine/fan/water loopers, random one-shots (pool, vents...)
    const em = MAP_EMITTERS[world.map];
    if (em) {
      for (const e of em.loopers) this.playInternal(e.alias, [e.origin[0], e.origin[1], e.origin[2]], -1, null, 1, 0);
      for (const e of em.lines) {
        const p: Vec3 = [0, 0, 0];
        // soundlineemitter: the loop sits at the point of the segment closest to the listener (ESTIMATE of the
        // engine's line-emitter behaviour)
        this.playInternal(e.alias, null, -1, () => closestOnSegment(p, e.a, e.b, this.listener), 1, 0);
      }
      this.randoms = em.randoms.map((r) => ({ ...r, next: -1 }));
    }
    // autoFx emitters need the createfx placements, which are not dumped (research/audio.md §5)
  }

  private randoms: { alias: string; origin: [number, number, number]; waitMin: number; waitMax: number; next: number }[] = [];
  /** createsoundrandom(origin, alias, min, max): one-shot every randomfloatrange(min, max) s. */
  private randomEmitters(): void {
    const now = this.ctx?.currentTime ?? 0;
    for (const r of this.randoms) {
      if (r.next < 0) { r.next = now + randRange([r.waitMin, r.waitMax], this.rnd()); continue; }
      if (now < r.next) continue;
      r.next = now + randRange([r.waitMin, r.waitMax], this.rnd());
      this.playInternal(r.alias, [r.origin[0], r.origin[1], r.origin[2]], -1, null, 1, 0);
    }
  }

  // ---- match flow: countdown, timer beeps, music ------------------------------------------------
  private matchSounds(world: ClientWorld): void {
    const phase = world.phase;
    const sec = Math.ceil(Math.max(0, world.timeLeftMs) / 1000);
    // The countdown beeps (uin_start_count_down, _globallogic.gsc matchstarttimer) and the time-limit
    // clock (mpl_ui_timer_countdown at <= 10 s and on even seconds <= 30 s) come from the gametype as
    // SOUND events (#G3); only the client-side music states are driven here.
    if (phase === MatchPhase.PLAYING) {
      // musictimesout(): TIME_OUT music state on match_ending_very_soon (<= 30 s)
      if (!this.timeOutMusic && sec <= 30 && sec > 0 && this.lastPhase === MatchPhase.PLAYING) {
        this.timeOutMusic = true;
        this.musicState('TIME_OUT');
      }
    } else if (phase >= MatchPhase.POSTGAME && !this.endMusic) {
      this.endMusic = true;
      const rows = [...world.scoreboard].sort((a, b) => b.points - a.points || a.deaths - b.deaths);
      const top = rows[0];
      const draw = rows.length > 1 && rows[1]!.points === top?.points;
      this.musicState(draw ? 'DRAW' : top?.clientNum === world.localClientNum ? 'VICTORY' : 'LOSE');
    }
    if (phase < MatchPhase.POSTGAME) this.endMusic = false;
    if (phase !== MatchPhase.PLAYING) this.timeOutMusic = false;
    this.lastTimerSec = sec;
    this.lastPhase = phase;
  }

  private musicVoice: number | null = null;
  /** clientscripts/mp/_music.csc states (manifest.musicStates); missing aliases stay silent. */
  musicState(state: string): void {
    const ops = this.manifest?.musicStates[state];
    if (!ops) return;
    for (const op of ops) {
      if ((op.op === 'musicalias' || op.op === 'musicaliasloop') && op.alias && op.exists !== false) {
        if (this.musicVoice !== null) this.stop(this.musicVoice);
        this.musicVoice = this.playInternal(op.alias, null, -1, null, 1, 0);
        return;
      }
    }
  }

  // ---- _healthoverlay.gsc breathing / heartbeat below 35 % health ------------------------------
  private healthSounds(world: ClientWorld): void {
    const ps = world.predicted;
    const now = this.ctx!.currentTime;
    const hc = this.healthClock;
    const dead = ps.pm_type === PmType.DEAD || ps.health <= 0;
    const hurt = !dead && ps.maxHealth > 0 && ps.health < ps.maxHealth * 0.35;
    if (!hurt) { hc.hurtSince = -1; hc.heartWait = 0.2; return; }
    if (hc.hurtSince < 0) { hc.hurtSince = now; hc.breathAt = now + 0.2; hc.heartAt = now + 0.2; }
    if (now >= hc.heartAt) {
      // playerheartbeatsound: wait 0.2 + hearbeatwait (0.2, +0.1 per beat up to 0.7)
      this.playInternal('mpl_player_heartbeat', null, -1, null, 1, 0);
      hc.heartAt = now + 0.2 + hc.heartWait;
      if (hc.heartWait <= 0.6) hc.heartWait += 0.1;
    }
    if (now >= hc.breathAt) {
      // playerbreathingsound -> battle chatter vox_<voice>_breathing_hurt (2D); only with > 1 player
      if (world.players.length > 0) {
        const me = world.localClientNum;
        this.playInternal(`${voicePrefix(this.bodyOf(world, me), me)}_breathing_hurt`, null, -1, null, 1, 0);
      }
      hc.breathAt = now + 0.2 + 0.784 + 0.1 + this.rnd() * 0.8;
    }
  }

  /** Test hook. */
  setRandom(r: () => number): void { this.rnd = r; }
  get voiceCount(): number { return this.voices.size; }
  /** Names of the live voices (debug/test). */
  get voiceNames(): string[] { return [...this.voices.values()].map((v) => v.name); }
  /** Every alias started since load (debug/test, capped). */
  readonly played: string[] = [];
  get state(): string { return this.ctx?.state ?? 'none'; }
  get manifestLoaded(): boolean { return !!this.manifest; }
}
